import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, mergeProps as _mergeProps, createVNode as _createVNode } from "vue";
const _hoisted_1 = { class: "mt-4 sm:mt-8" };
const _hoisted_2 = { class: "text-sm text-gray-600 mb-6" };
const _hoisted_3 = {
    key: 0,
    class: "mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700",
    role: "alert"
};
const _hoisted_4 = { class: "grid grid-cols-1 md:grid-cols-2 gap-6" };
const _hoisted_5 = { class: "md:col-span-2" };
import { AutoComplete, TextInput } from "@eudonet/opal";
import { buildAutoCompleteLabels } from "@/shared/composables/autoCompleteLabels";
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { currencyLabel } from "@/shared/utils/workspaceLocaleLabels";
import { useAddressForm } from "../../../../organisation/composables/useAddressForm";
import AddressDetailsModal from "../../../../organisation/presentation/components/AddressDetailsModal.vue";
import SharedEmailSettingSection from "./SharedEmailSettingSection.vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'GlobalParametersTab',
    props: {
        state: {},
        errors: {},
        locale: {}
    },
    emits: ["update:field", "blur:field"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const addressForm = useAddressForm();
        const { locale: uiLocale } = useI18n();
        const currencyValue = computed(() => props.locale?.currency ? currencyLabel(props.locale.currency, uiLocale.value) : "");
        // "Add more details" link surfaces once the user has typed an address but
        // hasn't selected an autocomplete suggestion yet (mirrors CreateOrganisationModal).
        const showAddressDetailsLink = computed(() => Boolean(addressForm.address.value) && !addressForm.addressDetailsSaved.value);
        // Parent → local: hydrate the composable when the parent state changes
        // (initial load + reset after save).
        watch(() => props.state.address, (next) => {
            if (next !== addressForm.address.value) {
                addressForm.address.value = next ?? "";
            }
        }, { immediate: true });
        // Local → parent: forward user edits to the parent's state via update:field.
        watch(addressForm.address, (next) => {
            if (next !== props.state.address) {
                emit("update:field", "address", next);
            }
        });
        function onFieldUpdate(field, value) {
            emit("update:field", field, value);
        }
        function onFieldBlur(field) {
            emit("blur:field", field);
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("p", _hoisted_2, _toDisplayString(_ctx.$t("workspace.settings.globalParameters.intro")), 1 /* TEXT */),
                (__props.errors.identifiers)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(_ctx.$t(__props.errors.identifiers)), 1 /* TEXT */))
                    : _createCommentVNode("v-if", true),
                _createElementVNode("div", _hoisted_4, [
                    _createElementVNode("div", _hoisted_5, [
                        _createVNode(_unref(AutoComplete), _mergeProps(_unref(buildAutoCompleteLabels)(_ctx.$t), {
                            modelValue: _unref(addressForm).address.value,
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((_unref(addressForm).address.value) = $event)),
                            label: _ctx.$t('workspace.settings.fields.address'),
                            placeholder: _ctx.$t('organisation.create.fields.addressPlaceholder'),
                            options: _unref(addressForm).addressSuggestions.value,
                            loading: _unref(addressForm).addressLoading.value,
                            "show-add-more-details": showAddressDetailsLink.value,
                            "add-more-details-label": _ctx.$t('organisation.create.fields.addressAddMoreDetails'),
                            "keep-input-on-edit": "",
                            "full-width": "",
                            onSearch: _unref(addressForm).onAddressSearch,
                            onSelect: _unref(addressForm).onAddressSelect,
                            onAddMoreDetails: _unref(addressForm).openAddressDetailsModal
                        }), null, 16 /* FULL_PROPS */, ["modelValue", "label", "placeholder", "options", "loading", "show-add-more-details", "add-more-details-label", "onSearch", "onSelect", "onAddMoreDetails"])
                    ]),
                    _createVNode(_unref(TextInput), {
                        "model-value": __props.state.siren,
                        label: _ctx.$t('workspace.settings.fields.siren'),
                        hint: _ctx.$t('workspace.settings.fields.sirenHint'),
                        placeholder: _ctx.$t('workspace.settings.fields.sirenPlaceholder'),
                        error: __props.errors.siren ? _ctx.$t(__props.errors.siren) : undefined,
                        maxlength: 9,
                        inputmode: "numeric",
                        autocomplete: "off",
                        "full-width": "",
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => (onFieldUpdate('siren', $event))),
                        onBlur: _cache[2] || (_cache[2] = ($event) => (onFieldBlur('siren')))
                    }, null, 8 /* PROPS */, ["model-value", "label", "hint", "placeholder", "error"]),
                    _createVNode(_unref(TextInput), {
                        "model-value": __props.state.siret,
                        label: _ctx.$t('workspace.settings.fields.siret'),
                        hint: _ctx.$t('workspace.settings.fields.siretHint'),
                        placeholder: _ctx.$t('workspace.settings.fields.siretPlaceholder'),
                        error: __props.errors.siret ? _ctx.$t(__props.errors.siret) : undefined,
                        maxlength: 14,
                        inputmode: "numeric",
                        autocomplete: "off",
                        "full-width": "",
                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => (onFieldUpdate('siret', $event))),
                        onBlur: _cache[4] || (_cache[4] = ($event) => (onFieldBlur('siret')))
                    }, null, 8 /* PROPS */, ["model-value", "label", "hint", "placeholder", "error"]),
                    _createVNode(_unref(TextInput), {
                        "model-value": __props.state.rna,
                        label: _ctx.$t('workspace.settings.fields.rna'),
                        hint: _ctx.$t('workspace.settings.fields.rnaHint'),
                        placeholder: _ctx.$t('workspace.settings.fields.rnaPlaceholder'),
                        error: __props.errors.rna ? _ctx.$t(__props.errors.rna) : undefined,
                        maxlength: 10,
                        autocomplete: "off",
                        "full-width": "",
                        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => (onFieldUpdate('rna', $event))),
                        onBlur: _cache[6] || (_cache[6] = ($event) => (onFieldBlur('rna')))
                    }, null, 8 /* PROPS */, ["model-value", "label", "hint", "placeholder", "error"]),
                    _createVNode(_unref(TextInput), {
                        "model-value": currencyValue.value,
                        label: _ctx.$t('workspace.settings.globalParameters.currency.label'),
                        placeholder: _ctx.$t('workspace.settings.globalParameters.notAvailable'),
                        readonly: "",
                        "full-width": "",
                        "data-testid": "workspace-settings-currency"
                    }, null, 8 /* PROPS */, ["model-value", "label", "placeholder"])
                ]),
                _createVNode(AddressDetailsModal, {
                    modelValue: _unref(addressForm).showAddressDetailsModal.value,
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event) => ((_unref(addressForm).showAddressDetailsModal.value) = $event)),
                    "initial-details": _unref(addressForm).addressDetails,
                    onSave: _unref(addressForm).saveAddressDetails
                }, null, 8 /* PROPS */, ["modelValue", "initial-details", "onSave"]),
                _createVNode(SharedEmailSettingSection)
            ]));
        };
    }
});
